'use es6';

import { useMemo, useCallback, useState } from 'react';
import { useMutation } from 'data-fetching-client';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { GREAT_WHITE } from 'HubStyleTokens/colors';
import UIExpandableText from 'UIComponents/text/UIExpandableText';
import UISection from 'UIComponents/section/UISection';
import UIList from 'UIComponents/list/UIList';
import UIButton from 'UIComponents/button/UIButton';
import Review from 'marketplace-ui-reviews/records/Review';
import Rating from 'marketplace-ui-reviews/components/Rating';
import ReviewBody from 'marketplace-ui-reviews/components/ReviewBody';
import ReviewerInfo from 'marketplace-ui-reviews/components/ReviewerInfo';
import ReviewReplyThread from 'marketplace-ui-reviews/components/ReviewReplyThread';
import ReviewActionsWrapper from 'marketplace-ui-reviews/components/ReviewActionsWrapper';
import HelpfulButton from 'marketplace-ui-reviews/components/HelpfulButton';
import ReportButton from 'marketplace-ui-reviews/components/ReportButton';
import ReportReviewModal from 'marketplace-ui-reviews/components/modals/ReportReviewModal';
import { Reportables } from 'marketplace-ui-reviews/constants/ReportReasons';
import { RatingAxes } from 'marketplace-ui-reviews/constants/RatingAxes';
import { REPORT_REVIEW } from 'marketplace-ui-reviews/queries/reportReviewQuery';
import { REPORT_REPLY } from 'marketplace-ui-reviews/queries/reportReplyQuery';
import { SET_REVIEW_HELPFUL } from 'marketplace-ui-reviews/queries/setReviewHelpfulQuery';
import { getUserMarkedHelpful } from 'marketplace-ui-reviews/utils/getUserMarkedHelpful';
import MediaQuery from 'marketplace-ui-common/layout/components/MediaQuery';
import { getIsCustomer, getIsUngatedForReviewEditing, getUserId } from '../../selectors/auth';
import { getServices } from '../../slices/serviceCatalogSlice';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ProfileReviewRenderer = ({
  hasProfile,
  review,
  searchTerm
}) => {
  const {
    createdAt,
    lastEditedAt,
    reply
  } = review;
  const [reportReviewModalOpen, setReportReviewModalOpen] = useState(false);
  const [reportReplyModalOpen, setReportReplyModalOpen] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const userId = useSelector(getUserId);
  const userMarkedHelpful = getUserMarkedHelpful(userId, review);
  const [localMarkedHelpful, setLocalMarkedHelpful] = useState(userMarkedHelpful);
  const isCustomer = useSelector(getIsCustomer);
  const catalogServices = useSelector(getServices);
  const isUngatedForReviewEditing = useSelector(getIsUngatedForReviewEditing);
  const [reportReview] = useMutation(REPORT_REVIEW);
  const [reportReply] = useMutation(REPORT_REPLY);
  const [setIsHelpful] = useMutation(SET_REVIEW_HELPFUL);
  const handleClickHelpful = useCallback(() => {
    return new Promise((resolve, reject) => {
      setIsHelpful({
        variables: {
          reviewId: review.get('id'),
          isHelpful: !localMarkedHelpful
        }
      }).then(() => {
        setLocalMarkedHelpful(!localMarkedHelpful);
        resolve();
      }).catch(err => {
        reject(err);
      });
    });
  }, [localMarkedHelpful, review, setIsHelpful]);
  const handleReportReplyConfirm = useCallback(({
    reason,
    comment
  }) => {
    return new Promise((resolve, reject) => {
      reportReply({
        variables: {
          reviewId: review.get('id'),
          replyId: review.get('reply').get('id'),
          reportReason: reason,
          comment
        }
      }).then(() => {
        resolve();
        setReportReplyModalOpen(false);
      }).catch(err => {
        reject(err);
      });
    });
  }, [reportReply, review]);
  const handleReportReviewConfirm = useCallback(({
    reason,
    comment
  }) => {
    return new Promise((resolve, reject) => {
      reportReview({
        variables: {
          reviewId: review.get('id'),
          reportReason: reason,
          comment
        }
      }).then(() => {
        resolve();
        setReportReviewModalOpen(false);
      }).catch(err => {
        reject(err);
      });
    });
  }, [reportReview, review]);
  const renderedReportReviewModal = useMemo(() => /*#__PURE__*/_jsx(ReportReviewModal, {
    catalogServices: catalogServices,
    review: review,
    type: Reportables.REVIEW,
    onClose: () => setReportReviewModalOpen(false),
    onConfirm: handleReportReviewConfirm
  }), [catalogServices, review, handleReportReviewConfirm]);
  const renderedReportReplyModal = useMemo(() => /*#__PURE__*/_jsx(ReportReviewModal, {
    catalogServices: catalogServices,
    review: review,
    type: Reportables.REPLY,
    onClose: () => setReportReplyModalOpen(false),
    onConfirm: handleReportReplyConfirm
  }), [catalogServices, review, handleReportReplyConfirm]);
  const helpfulCount = useMemo(() => {
    const currentHelpfulCount = review.getHelpfulCount();
    if (userMarkedHelpful && !localMarkedHelpful) {
      return currentHelpfulCount > 0 ? currentHelpfulCount - 1 : 0;
    }
    if (!userMarkedHelpful && localMarkedHelpful) {
      return currentHelpfulCount + 1;
    }
    return currentHelpfulCount;
  }, [localMarkedHelpful, review, userMarkedHelpful]);
  const reviewActions = useMemo(() => {
    const helpfulDisabled = hasProfile || !isCustomer;
    const helpfulTooltip = hasProfile ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ecosystem-marketplace-solutions-ui.profile.reviews.helpful.isProvider"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ecosystem-marketplace-solutions-ui.profile.reviews.helpful.isPublic"
    });
    return /*#__PURE__*/_jsxs(ReviewActionsWrapper, {
      children: [/*#__PURE__*/_jsx(HelpfulButton, {
        className: "m-right-2",
        disabled: helpfulDisabled,
        helpfulCount: helpfulCount,
        onClickHelpful: handleClickHelpful,
        tooltip: helpfulTooltip,
        userMarkedHelpful: localMarkedHelpful
      }), /*#__PURE__*/_jsx(ReportButton, {
        className: "m-left-2",
        isCustomer: isCustomer,
        onClick: () => setReportReviewModalOpen(true)
      }), isUngatedForReviewEditing && lastEditedAt && review.get('original').ratings.get('OVERALL') !== review.get('ratings').get('OVERALL') && /*#__PURE__*/_jsx(UIButton, {
        className: "m-left-2",
        onClick: () => setShowOriginal(!showOriginal),
        use: "link",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `ecosystem-marketplace-solutions-ui.profile.reviews.edit.${showOriginal ? 'showUpdated' : 'showOriginal'}`
        })
      })]
    });
  }, [hasProfile, isCustomer, isUngatedForReviewEditing, helpfulCount, handleClickHelpful, lastEditedAt, localMarkedHelpful, review, showOriginal]);
  return /*#__PURE__*/_jsxs("div", {
    className: "p-y-8",
    style: {
      width: '100%',
      borderBottom: `1px solid ${GREAT_WHITE}`
    },
    children: [/*#__PURE__*/_jsx(UISection, {
      children: /*#__PURE__*/_jsx(Rating, {
        direction: "column",
        rating: showOriginal ? review.get('original').ratings.get(RatingAxes.OVERALL) : review.getIn(['ratings', RatingAxes.OVERALL]),
        label: /*#__PURE__*/_jsxs(UIList, {
          inline: true,
          use: "inline-divided",
          children: [/*#__PURE__*/_jsx(FormattedDateTime, {
            value: createdAt,
            format: "ll",
            type: "userTz"
          }), isUngatedForReviewEditing && lastEditedAt && /*#__PURE__*/_jsxs("span", {
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              className: "m-right-1",
              message: "ecosystem-marketplace-solutions-ui.profile.reviews.edit.edited"
            }), /*#__PURE__*/_jsx(FormattedDateTime, {
              value: lastEditedAt,
              format: "ll",
              type: "userTz"
            })]
          })]
        }),
        showTextRating: true
      })
    }), /*#__PURE__*/_jsx(MediaQuery, {
      minScreenSize: "md",
      alternate: /*#__PURE__*/_jsx(UIExpandableText, {
        maxHeight: 330,
        hideButtonCaret: true,
        children: /*#__PURE__*/_jsx(ReviewBody, {
          review: review,
          searchTerm: searchTerm,
          showOriginal: showOriginal,
          catalogServices: [...catalogServices]
        })
      }),
      children: /*#__PURE__*/_jsx(ReviewBody, {
        review: review,
        searchTerm: searchTerm,
        showOriginal: showOriginal,
        catalogServices: catalogServices
      })
    }), /*#__PURE__*/_jsx(ReviewerInfo, {
      review: review,
      inline: true
    }), reviewActions, /*#__PURE__*/_jsx(ReviewReplyThread, {
      reply: reply,
      onReportReplyClick: isCustomer ? () => setReportReplyModalOpen(true) : undefined
    }), reportReviewModalOpen && renderedReportReviewModal, reportReplyModalOpen && renderedReportReplyModal]
  });
};
ProfileReviewRenderer.propTypes = {
  hasProfile: PropTypes.bool,
  review: PropTypes.instanceOf(Review).isRequired,
  searchTerm: PropTypes.string
};
export default ProfileReviewRenderer;